import { detect } from 'detect-browser'
import * as React from 'react'
import Loadable from 'react-loadable'

import { RETRY_OPTIONS } from '@disco/api/server/constants'
import captureError from '@disco/utils/captureError'
import { retryPromise } from '@disco/utils/promise'

/**
 * Tracks the current retry attempt for HLS.js loading.
 * Used for logging and error reporting.
 */
let hlsLoadAttempt = 0

const PlaybackManager: any = Loadable.Map({
  loader: {
    // @ts-ignore
    Component: () => import('./index'),
    Hls: () => {
      const browser = detect()
      if (browser?.os === 'iOS' || browser?.name === 'safari')
        return Promise.resolve()

      // Reset attempt counter for new load
      hlsLoadAttempt = 0

      // Retry HLS.js chunk loading with exponential backoff
      // Uses default RETRY_OPTIONS: 5 retries, 2x backoff factor, 1s-30s timeout range
      // This addresses transient network issues, CDN failures, and webpack chunk loading timeouts
      // See SLAM-1115 for details
      return retryPromise(
        () => {
          hlsLoadAttempt++
          if (hlsLoadAttempt > 1) {
            console.log(
              `[HLS Loader] Retry attempt ${
                hlsLoadAttempt - 1
              } for HLS.js chunk loading`,
            )
          }

          // consume minified build due issues with
          // hls.js web worker handoff and minifiers
          // https://github.com/video-dev/hls.js/issues/5107
          return import(
            /* webpackChunkName: "hls" */
            'hls.js/dist/hls.light.min.js'
          ).then(({ default: Hls }) => {
            if (hlsLoadAttempt > 1) {
              console.log(
                `[HLS Loader] Successfully loaded HLS.js on attempt ${hlsLoadAttempt}`,
              )
            }
            window.Hls = Hls
          })
        },
        undefined,
        RETRY_OPTIONS,
      ).catch(error => {
        // Only report error after all retry attempts exhausted
        const errorMessage = `Failed to lazy load HLS.js after ${hlsLoadAttempt} attempts`
        console.error(`[HLS Loader] ${errorMessage}`, {
          attempts: hlsLoadAttempt,
          browser: browser?.name,
          os: browser?.os,
          browserVersion: browser?.version,
          error,
        })
        captureError(new Error(errorMessage), {
          extra: {
            attempts: hlsLoadAttempt,
            browserName: browser?.name,
            browserOS: browser?.os,
            browserVersion: browser?.version,
          },
        })
      })
    },
  },
  loading: () => null,

  render(loaded, props) {
    let Component = loaded.Component.default
    return <Component {...props} />
  },
})
export default PlaybackManager
